/* ------------------------------------------------------------------ */
/*  Chart-type catalog + glyphs                                         */
/*                                                                      */
/*  Moved out of Toolbar.tsx: every surface that lists chart types — the */
/*  toolbar dropdown, the body context menu, and the app's panel-header  */
/*  pill — imports this catalog, and react-refresh requires .tsx files   */
/*  to export only components for HMR to stay reliable.                  */
/* ------------------------------------------------------------------ */

export interface ChartTypeEntry {
  name: string
  label: string
  /** Listed in every picker with a Beta chip. Picking a chart type is already
   *  an explicit opt-in, so a Beta family has no separate Settings gate. */
  beta?: true
}

/**
 * The chart-type families offered in the toolbar.
 *
 * Deliberately five entries, not seven: hollow-vs-solid candles and area-vs-plain
 * line are MODIFIERS, toggled in Settings (see `isHollowCandles` /
 * `isAreaFilled` in chartSettings.ts), not families of their own. `area` and
 * `line` were never even distinct chart types — both are klinecharts' `area`
 * series differing only in fill (chartTypeStyles.ts). Heikin Ashi has the same
 * solid/hollow modifier (`heikin_ashi_hollow`).
 *
 * The retired `candle_stroke` / `area` strings remain fully valid chart types and
 * are still what the modifiers write, so saved layouts holding them keep working
 * — `styleGroupOf` maps them back to their family on read. Nothing is migrated.
 */
export const CHART_TYPES: readonly ChartTypeEntry[] = [
  { name: 'candle_solid', label: 'Candles' },
  { name: 'ohlc', label: 'Bars' },
  { name: 'line', label: 'Line' },
  // TPO replaces the candles with one Market Profile per session plus a thin
  // close-price line — see candleStylesForChartType and utils/tpoIndicator.ts.
  { name: 'tpo', label: 'TPO' },
  // Heikin Ashi: averaged candles painted by an internal indicator over a
  // hidden native series, so every other indicator still reads raw bars —
  // see utils/heikinAshiIndicator.ts. Six-character label: the pickers cap at 10.
  // Listed plainly (no Beta chip) by user decision 2026-09-08.
  { name: 'heikin_ashi', label: 'Heikin' },
]

// ── chart-type glyphs (24x24 SVG path strings) ──
//
// These deliberately fill most of the 24x24 viewBox. The first drafts drew
// inside roughly the middle half of the box — the TPO one spanned only x 4-15,
// y 6-18, about 46% of the area — so beside lucide icons, which fill their box,
// they rendered visibly smaller at the same nominal size. Keep new glyphs
// roughly within x 3-21, y 3-21 so they carry the same optical weight.
const GLYPH_CANDLE = 'M7.5 3v4M7.5 15v6M16.5 3v5M16.5 17v4M5 7h5v8H5zM14 8h5v9h-5z'
// A smooth wave rather than the old angular polyline — at menu sizes the
// zigzag read as a generic chevron, not as a price line.
const GLYPH_LINE = 'M3 12q4.5-8 9 0t9 0'
const GLYPH_AREA = 'M3 20L8 14l4 4 9-11v13H3z'
/** OHLC: two vertical bars with open (left) and close (right) ticks. */
const GLYPH_BARS = 'M8 4v16M8 8H4M8 13h4M17 7v13M17 11h-4M17 16h4'
/** TPO: stacked profile rows, tapering — the same visual language as the
 *  Style-section TPO card in SettingsModal, so the two surfaces agree. */
const GLYPH_TPO = 'M3 5h9v5H3zM3 10h17v5H3zM3 15h11v5H3z'
/** Heikin Ashi: three stepped, same-direction bodies with short wicks — the
 *  "smoothed trend" read that distinguishes it from the two-candle glyph. */
const GLYPH_HEIKIN =
  'M5 8v-3M5 15v3M11 6v-3M11 17v2M17 5v-2M17 19v2M3 8h4v7H3zM9 6h4v11H9zM15 5h4v14h-4z'

/**
 * Glyph per chart type, including the retired modifier strings.
 *
 * `candle_stroke` and `area` are still valid persisted values (see the note on
 * CHART_TYPES), so they map to their family's icon rather than falling through to
 * a default — a chart restored on hollow candles must still show a candle glyph.
 */
const CHART_TYPE_ICONS: Record<string, string> = {
  candle_solid: GLYPH_CANDLE,
  candle_stroke: GLYPH_CANDLE,
  ohlc: GLYPH_BARS,
  line: GLYPH_LINE,
  area: GLYPH_AREA,
  tpo: GLYPH_TPO,
  heikin_ashi: GLYPH_HEIKIN,
  heikin_ashi_hollow: GLYPH_HEIKIN,
}

export function chartTypeIcon(chartType: string | undefined): string {
  return (chartType && CHART_TYPE_ICONS[chartType]) || GLYPH_CANDLE
}
